import { useMemo, useRef, useState } from "react";
import {
  Check, Eraser, PenTool, Redo2, RotateCw, Trash2, Undo2, ZoomIn, ZoomOut,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useLang } from "@/context/LanguageContext";
import { inputCls } from "@/components/shared";

const VB_W = 800;
const VB_H = 560;

export function computeGeometry(points, lengths) {
  const n = points.length;
  if (n < 3) return null;
  const pix = points.map((p, i) => {
    const q = points[(i + 1) % n];
    return Math.hypot(q.x - p.x, q.y - p.y) || 0.0001;
  });
  const entries = Object.entries(lengths).filter(([i, v]) => Number(v) > 0 && Number(i) < n);
  const scale = entries.length
    ? entries.reduce((s, [i, v]) => s + Number(v) / pix[Number(i)], 0) / entries.length
    : 0.02;
  const edgeMeters = pix.map((d, i) => (Number(lengths[i]) > 0 ? Number(lengths[i]) : d * scale));
  // Rebuild the polygon in metre-space, keeping the drawn directions
  const real = [{ x: 0, y: 0 }];
  for (let i = 0; i < n; i++) {
    const p = points[i];
    const q = points[(i + 1) % n];
    const d = pix[i];
    const last = real[real.length - 1];
    real.push({ x: last.x + ((q.x - p.x) / d) * edgeMeters[i], y: last.y + ((q.y - p.y) / d) * edgeMeters[i] });
  }
  const gap = Math.hypot(real[n].x - real[0].x, real[n].y - real[0].y);
  const poly = real.slice(0, n);
  let area = 0;
  for (let i = 0; i < n; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % n];
    area += a.x * b.y - b.x * a.y;
  }
  area = Math.abs(area) / 2;
  const perimeter = edgeMeters.reduce((s, v) => s + v, 0);
  const xs = poly.map((p) => p.x);
  const ys = poly.map((p) => p.y);
  return {
    area: Math.round(area * 10) / 10,
    perimeter: Math.round(perimeter * 100) / 100,
    maxWidth: Math.round((Math.max(...xs) - Math.min(...xs)) * 100) / 100,
    maxLength: Math.round((Math.max(...ys) - Math.min(...ys)) * 100) / 100,
    gap, scale, edgeMeters,
  };
}

const wallLetter = (i) => String.fromCharCode(65 + (i % 26));

const ToolBtn = ({ onClick, disabled, active, testId, title, children }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium transition-colors disabled:opacity-30 ${
      active ? "bg-[#2F4F4F] text-white" : "bg-white text-stone-600 border border-stone-200 hover:bg-stone-50"
    }`}
  >
    {children}
  </button>
);

export const RoomSketch = ({ room, onClose, onSave }) => {
  const { t } = useLang();
  const [points, setPoints] = useState((room.sketch?.points || []).map((p) => ({ ...p })));
  const [lengths, setLengths] = useState({ ...(room.sketch?.lengths || {}) });
  const [drawing, setDrawing] = useState(!(room.sketch?.points?.length >= 3));
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [rot, setRot] = useState(0);
  const [history, setHistory] = useState([]);
  const [future, setFuture] = useState([]);
  const [edgeEdit, setEdgeEdit] = useState(null); // {idx, value}
  const [selected, setSelected] = useState(null);
  const svgRef = useRef(null);
  const dragRef = useRef(null);

  const geo = useMemo(() => computeGeometry(points, lengths), [points, lengths]);
  const n = points.length;

  const snapshot = () => {
    setHistory((h) => [...h, { points: points.map((p) => ({ ...p })), lengths: { ...lengths } }].slice(-60));
    setFuture([]);
  };
  const undo = () => {
    if (!history.length) return;
    setFuture((f) => [{ points, lengths }, ...f]);
    const last = history[history.length - 1];
    setPoints(last.points);
    setLengths(last.lengths);
    setHistory((h) => h.slice(0, -1));
    setSelected(null);
    setEdgeEdit(null);
  };
  const redo = () => {
    if (!future.length) return;
    setHistory((h) => [...h, { points, lengths }]);
    const next = future[0];
    setPoints(next.points);
    setLengths(next.lengths);
    setFuture((f) => f.slice(1));
  };

  const toCanvas = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    let x = ((e.clientX - rect.left) / rect.width) * VB_W;
    let y = ((e.clientY - rect.top) / rect.height) * VB_H;
    if (rot) {
      const cx = VB_W / 2, cy = VB_H / 2, a = (-rot * Math.PI) / 180;
      const dx = x - cx, dy = y - cy;
      x = cx + dx * Math.cos(a) - dy * Math.sin(a);
      y = cy + dx * Math.sin(a) + dy * Math.cos(a);
    }
    return { x: (x - offset.x) / zoom, y: (y - offset.y) / zoom };
  };

  const onBgPointerDown = (e) => {
    if (drawing) {
      snapshot();
      setPoints([...points, toCanvas(e)]);
      return;
    }
    dragRef.current = { type: "pan", startX: e.clientX, startY: e.clientY, orig: { ...offset } };
    setSelected(null);
  };

  const onPointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag) return;
    if (drag.type === "pan") {
      const rect = svgRef.current.getBoundingClientRect();
      setOffset({
        x: drag.orig.x + ((e.clientX - drag.startX) / rect.width) * VB_W,
        y: drag.orig.y + ((e.clientY - drag.startY) / rect.height) * VB_H,
      });
    } else if (drag.type === "corner") {
      const c = toCanvas(e);
      setPoints(points.map((p, i) => (i === drag.idx ? c : p)));
    }
  };

  const onPointerUp = () => { dragRef.current = null; };

  const startCornerDrag = (e, i) => {
    e.stopPropagation();
    snapshot();
    dragRef.current = { type: "corner", idx: i };
    setSelected(i);
  };

  const addCorner = (edgeIdx) => {
    snapshot();
    const a = points[edgeIdx];
    const b = points[(edgeIdx + 1) % n];
    const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    const next = [...points];
    next.splice(edgeIdx + 1, 0, mid);
    const newLengths = {};
    Object.entries(lengths).forEach(([k, v]) => {
      const ki = Number(k);
      if (ki < edgeIdx) newLengths[ki] = v;
      else if (ki > edgeIdx) newLengths[ki + 1] = v;
    });
    setPoints(next);
    setLengths(newLengths);
    setEdgeEdit(null);
  };

  const deleteCorner = () => {
    if (selected == null || n <= 3) return;
    snapshot();
    const i = selected;
    const prevEdge = (i - 1 + n) % n;
    const newLengths = {};
    Object.entries(lengths).forEach(([k, v]) => {
      const ki = Number(k);
      if (ki === prevEdge || ki === i) return;
      newLengths[ki > i ? ki - 1 : ki] = v;
    });
    setPoints(points.filter((_, idx) => idx !== i));
    setLengths(newLengths);
    setSelected(null);
    setEdgeEdit(null);
  };

  const clearAll = () => {
    snapshot();
    setPoints([]);
    setLengths({});
    setDrawing(true);
    setSelected(null);
    setEdgeEdit(null);
  };

  const setWallLength = () => {
    if (!edgeEdit) return;
    snapshot();
    const next = { ...lengths };
    if (Number(edgeEdit.value) > 0) next[edgeEdit.idx] = Number(edgeEdit.value);
    else delete next[edgeEdit.idx];
    setLengths(next);
    setEdgeEdit(null);
  };

  const save = () => {
    onSave({
      points,
      lengths,
      area: geo?.area ?? 0,
      perimeter: geo?.perimeter ?? 0,
    });
  };

  const closed = !drawing && n >= 3;
  const edgeCount = closed ? n : Math.max(0, n - 1);
  const notClosed = geo && geo.gap > 0.02 * geo.perimeter;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl w-[96vw] max-h-[94dvh] bg-white p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl sm:text-2xl font-medium text-stone-900">
            {t("sketchRoom")} — {room.name}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-1.5">
          <ToolBtn testId="sketch-draw-button" active={drawing} onClick={() => { setDrawing(true); setSelected(null); }} title={t("drawRoom")}>
            <PenTool size={13} strokeWidth={1.5} /> {t("drawRoom")}
          </ToolBtn>
          <ToolBtn testId="sketch-finish-button" disabled={n < 3 || !drawing} onClick={() => setDrawing(false)} title={t("finishRoom")}>
            <Check size={13} strokeWidth={1.5} /> {t("finishRoom")}
          </ToolBtn>
          <ToolBtn testId="sketch-undo-button" disabled={!history.length} onClick={undo} title={t("undo")}><Undo2 size={13} /></ToolBtn>
          <ToolBtn testId="sketch-redo-button" disabled={!future.length} onClick={redo} title={t("redo")}><Redo2 size={13} /></ToolBtn>
          <ToolBtn testId="sketch-delete-corner" disabled={selected == null || drawing || n <= 3} onClick={deleteCorner} title={t("deleteCorner")}>
            <Trash2 size={13} /> {t("deleteCorner")}
          </ToolBtn>
          <ToolBtn testId="sketch-zoom-in" onClick={() => setZoom((z) => Math.min(z * 1.25, 8))}><ZoomIn size={13} /></ToolBtn>
          <ToolBtn testId="sketch-zoom-out" onClick={() => setZoom((z) => Math.max(z / 1.25, 0.2))}><ZoomOut size={13} /></ToolBtn>
          <ToolBtn testId="sketch-rotate" onClick={() => setRot((r) => (r + 15) % 360)}><RotateCw size={13} /></ToolBtn>
          <ToolBtn testId="sketch-clear-button" onClick={clearAll} title={t("clearSketch")}><Eraser size={13} /></ToolBtn>
        </div>

        {edgeEdit && (
          <div className="flex items-center gap-2 bg-stone-50 border border-stone-200 rounded-lg p-2.5">
            <span className="text-xs font-semibold text-stone-600 whitespace-nowrap">
              {t("lengthM").replace(" (m)", "")} {wallLetter(edgeEdit.idx)} (m):
            </span>
            <input
              data-testid="sketch-length-input"
              type="number"
              step="0.01"
              autoFocus
              className={`${inputCls} w-28`}
              value={edgeEdit.value}
              onChange={(e) => setEdgeEdit({ ...edgeEdit, value: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && setWallLength()}
            />
            <button data-testid="sketch-length-set" onClick={setWallLength}
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-4 py-2 text-xs font-medium transition-colors">OK</button>
            <button onClick={() => setEdgeEdit(null)} className="text-xs text-stone-400 hover:text-stone-700 px-2">{t("cancel")}</button>
          </div>
        )}

        <p className="text-[11px] text-stone-400">{drawing ? t("tapToDraw") : t("tapWallHint")}</p>

        <svg
          ref={svgRef}
          data-testid="sketch-svg"
          viewBox={`0 0 ${VB_W} ${VB_H}`}
          className="w-full bg-stone-50 border border-stone-200 rounded-xl select-none"
          style={{ touchAction: "none", height: "min(52vh, 480px)", cursor: drawing ? "crosshair" : "grab" }}
          onPointerDown={onBgPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
        >
          <defs>
            <pattern id="grid" width="24" height="24" patternUnits="userSpaceOnUse">
              <path d="M 24 0 L 0 0 0 24" fill="none" stroke="#E7E5E4" strokeWidth="1" />
            </pattern>
          </defs>
          <g transform={`rotate(${rot} ${VB_W / 2} ${VB_H / 2})`}>
            <g transform={`translate(${offset.x} ${offset.y}) scale(${zoom})`}>
              <rect x={-4000} y={-4000} width={8000} height={8000} fill="url(#grid)" pointerEvents="none" />
              {n >= 2 && (
                closed ? (
                  <polygon
                    points={points.map((p) => `${p.x},${p.y}`).join(" ")}
                    fill="rgba(47,79,79,0.10)"
                    stroke="#2F4F4F"
                    strokeWidth={2 / zoom}
                    pointerEvents="none"
                  />
                ) : (
                  <polyline
                    points={points.map((p) => `${p.x},${p.y}`).join(" ")}
                    fill="none"
                    stroke="#2F4F4F"
                    strokeWidth={2 / zoom}
                    strokeDasharray={`${6 / zoom} ${4 / zoom}`}
                    pointerEvents="none"
                  />
                )
              )}
              {/* Edge hit areas + labels + midpoint add handles */}
              {Array.from({ length: edgeCount }).map((_, i) => {
                const a = points[i];
                const b = points[(i + 1) % n];
                const mx = (a.x + b.x) / 2;
                const my = (a.y + b.y) / 2;
                const pixLen = Math.hypot(b.x - a.x, b.y - a.y);
                const meters = Number(lengths[i]) > 0 ? Number(lengths[i]) : geo ? pixLen * geo.scale : pixLen * 0.02;
                const exact = Number(lengths[i]) > 0;
                return (
                  <g key={`edge-${i}`}>
                    <line
                      data-testid={`sketch-edge-${i}`}
                      x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                      stroke="transparent"
                      strokeWidth={16 / zoom}
                      style={{ cursor: "pointer" }}
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        if (!drawing) setEdgeEdit({ idx: i, value: lengths[i] || "" });
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <g
                      data-testid={`sketch-edge-label-${i}`}
                      style={{ cursor: "pointer" }}
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        if (!drawing) setEdgeEdit({ idx: i, value: lengths[i] || "" });
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <rect
                        x={mx - 42 / zoom} y={my - 22 / zoom}
                        width={84 / zoom} height={20 / zoom}
                        fill="transparent"
                      />
                      <text
                        x={mx} y={my - 8 / zoom}
                        fontSize={12 / zoom}
                        fill={exact ? "#1C1917" : "#A8A29E"}
                        fontWeight={exact ? 600 : 400}
                        textAnchor="middle"
                        fontFamily="Manrope, sans-serif"
                      >
                        {wallLetter(i)}: {exact ? meters.toFixed(2) : `~${meters.toFixed(2)}`} m
                      </text>
                    </g>
                    {closed && (
                      <circle
                        data-testid={`sketch-add-corner-${i}`}
                        cx={mx} cy={my} r={5 / zoom}
                        fill="#FFFFFF" stroke="#A8A29E" strokeWidth={1 / zoom}
                        style={{ cursor: "copy" }}
                        onPointerDown={(e) => { e.stopPropagation(); addCorner(i); }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                  </g>
                );
              })}
              {/* Corner handles */}
              {points.map((p, i) => (
                <circle
                  key={`corner-${i}`}
                  data-testid={`sketch-corner-${i}`}
                  cx={p.x} cy={p.y}
                  r={(selected === i ? 9 : 7) / zoom}
                  fill={selected === i ? "#2F4F4F" : "#FFFFFF"}
                  stroke="#1E293B"
                  strokeWidth={1.5 / zoom}
                  style={{ cursor: "move" }}
                  onPointerDown={(e) => startCornerDrag(e, i)}
                />
              ))}
            </g>
          </g>
        </svg>

        <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
          <div>
            <p className="text-[11px] uppercase tracking-[0.12em] font-semibold text-stone-500">{t("areaLabel")}</p>
            <p data-testid="sketch-area-display" className="font-heading text-2xl font-medium text-stone-900">
              {geo ? geo.area.toFixed(1) : "0.0"} <span className="text-sm text-stone-400 font-body">m²</span>
            </p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.12em] font-semibold text-stone-500">{t("perimeter")}</p>
            <p data-testid="sketch-perimeter-display" className="text-lg font-medium text-stone-800">
              {geo ? geo.perimeter.toFixed(2) : "0.00"} m
            </p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.12em] font-semibold text-stone-500">Max</p>
            <p className="text-lg font-medium text-stone-800">
              {geo ? `${geo.maxWidth.toFixed(2)} × ${geo.maxLength.toFixed(2)} m` : "—"}
            </p>
          </div>
          {notClosed && (
            <p data-testid="sketch-not-closed-warning" className="text-xs text-[#9A3412] bg-orange-50 border border-orange-100 rounded-lg px-3 py-1.5">
              {t("notClosed")}
            </p>
          )}
          <div className="ml-auto flex gap-3">
            <button data-testid="sketch-cancel-button" onClick={onClose}
              className="bg-white text-stone-700 border border-stone-200 hover:bg-stone-50 rounded-lg px-5 py-2 text-sm font-medium transition-colors">{t("cancel")}</button>
            <button data-testid="sketch-save-button" onClick={save} disabled={n < 3}
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-5 py-2 text-sm font-medium transition-colors disabled:opacity-60">{t("save")}</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
